import BoardPanelTypes from 'app/stores/boards/boardPanelTypes';
import { defaultsDeep } from 'lodash';
import { Layout, Layouts } from 'react-grid-layout';
import $decks from 'app/stores/decks/$decks';
import { compact } from '@kentik/ui-shared/boards/layout';
import { isLayoutEqual, PanelData, PanelLayout } from '@kentik/ui-shared/boards/panels';
import type BoardPanelModel from './BoardPanelModel';

const panelDefaults: PanelLayout = {
  lg: {
    x: 0,
    y: 0,
    w: 4,
    h: 3,
    minW: 1,
    minH: 1,
    maxW: 12
  },
  sm: {
    x: 0,
    y: 0,
    w: 1,
    h: 2,
    minW: 1,
    minH: 1,
    maxW: 1
  }
};

function getPanelLayoutDefaults(panelData: Partial<PanelData>): PanelLayout | undefined {
  const { type } = panelData;

  let layout =
    type && BoardPanelTypes[type] && 'layoutDefaults' in BoardPanelTypes[type]
      ? (BoardPanelTypes[type].layoutDefaults as PanelLayout)
      : undefined;

  if (type === 'widget') {
    const widgetName = panelData.config?.widgetName;
    if (widgetName) {
      const { widgets } = $decks;
      layout = widgetName in widgets ? widgets[widgetName].layoutDefaults : undefined;
      layout = $decks.widgets[widgetName]?.layoutDefaults;
    }
  }

  return layout;
}

function findLayoutItem(layout: Layout[], id: string | number) {
  return layout.find((item) => `${item.i}` === `${id}`);
}

export function getPanelLayout(layouts: Layouts, id: string | number): PanelLayout | null {
  const sm = findLayoutItem(layouts.sm, id);
  const lg = findLayoutItem(layouts.lg, id);
  return sm && lg ? { sm, lg } : null;
}

export function generatePanelLayout(panelData: Partial<PanelData>): PanelLayout {
  const layout = defaultsDeep(panelData.layout, getPanelLayoutDefaults(panelData) || {}, panelDefaults);

  // constraints of layout
  layout.sm.w = 1;
  layout.sm.minW = 1;
  layout.sm.maxW = 1;
  layout.sm.maxH = 50;
  layout.lg.maxW = 12;
  layout.lg.maxH = 50;

  // make sure width/height is greater than min
  layout.sm.h = Math.max(layout.sm.h, layout.sm.minH || 1);
  layout.lg.h = Math.max(layout.lg.h, layout.lg.minH || 1);
  layout.lg.w = Math.max(layout.lg.w, layout.lg.minW || 1);

  return layout;
}

/**
 * Generates a panel layout that puts the new panel at the bottom of the board
 * @param layouts Existing layouts
 * @param panelData New panel data, used to determine layout defaults
 */
export function newPanelLayout(layouts: Layouts, panelData: Partial<PanelData>) {
  const newLayout = generatePanelLayout(panelData);

  // find the max y position in both layouts
  const maxYSm = layouts.sm.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const maxYLg = layouts.lg.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  // place the new panel at the bottom
  newLayout.sm.y = maxYSm;
  newLayout.lg.y = maxYLg;

  return newLayout;
}

export function updateLayout(panelModels: BoardPanelModel[], layouts: Layouts) {
  const updatedPanels: BoardPanelModel[] = [];

  panelModels.forEach((panelModel) => {
    const originalLayout = panelModel.get('layout');
    const panelLayout = getPanelLayout(layouts, panelModel.id);
    if (panelLayout && !isLayoutEqual(originalLayout, panelLayout)) {
      panelModel.set('layout', panelLayout);
      updatedPanels.push(panelModel);
    }
  });

  return updatedPanels;
}

export default function generateLayout(panelModels: BoardPanelModel[]): Layouts {
  const layouts: Layouts = { sm: [], lg: [] };

  panelModels.forEach((panelModel) => {
    const panelLayout = generatePanelLayout(panelModel.get());
    layouts.sm.push({ ...panelLayout.sm, i: `${panelModel.id}` });
    layouts.lg.push({ ...panelLayout.lg, i: `${panelModel.id}` });
  });

  layouts.sm = compact(layouts.sm, 'vertical', 1);
  layouts.lg = compact(layouts.lg, 'vertical', 12);

  return layouts;
}
